import SuperJSON from "superjson";
import { type ActorRefFromLogic, assign, createActor, fromPromise, setup, waitFor } from "xstate";

import { isDesktopAppMode } from "~/auth/interop/common";

// Type definitions
type QueryStatus = "idle" | "loading" | "error" | "success";

// Add data source tracking
type DataSource = "localStorage" | "persistence" | "remote" | null;

export interface QueryData<TData> {
	data: TData | null;
	cachedData: TData | null;
	error: Error | null;
	status: QueryStatus;
	isLoading: boolean;
	isError: boolean;
	isSuccess: boolean;
	dataSource: DataSource; // Track where the data came from
	shouldEnd: boolean;
	enabled: boolean;
}

export const emptyQueryData = <T>() =>
	({
		data: null,
		cachedData: null,
		error: null,
		status: "idle",
		isLoading: false,
		isError: false,
		isSuccess: false,
		dataSource: null,
		shouldEnd: false,
		enabled: false,
	}) satisfies QueryData<T>;

export interface QueryConfig<TData> {
	queryFn: () => Promise<TData>;
	enabled?: boolean;
	refetchInterval?: number;
	cacheTime?: number;
	queryKey: string;
	// Add persistence handlers
	persistData?: (data: TData, cachedData: TData) => Promise<void>;
	loadPersistedData?: () => Promise<TData | null>;
	shouldEnd?: boolean;
}

// Add cache helpers
function getFromCache<T>(queryKey: string): { data: T; timestamp: number } | null {
	try {
		const cached =
			typeof window !== "undefined" ? localStorage.getItem(`query-${queryKey}`) : null;
		if (cached) {
			return SuperJSON.parse(cached);
		}
	} catch (e) {
		console.warn("Failed to parse cached query data:", e);
	}
	return null;
}

function setCache<T>(queryKey: string, data: T) {
	try {
		if (typeof window !== "undefined") {
			localStorage.setItem(
				`query-${queryKey}`,
				SuperJSON.stringify({
					data,
					timestamp: Date.now(),
				})
			);
		}
	} catch (e) {
		console.warn("Failed to cache query data:", e);
	}
}

export async function waitForQueryRefetch<TData>(
	query: ActorRefFromLogic<ReturnType<typeof createQueryMachine<TData>>>
) {
	const fetching = waitFor(query, (state) => state.matches("fetching"));
	query.send({ type: "resume" });
	query.send({ type: "invalidate" });
	await fetching;
	return await waitFor(query, (state) => !state.matches("fetching"));
}

export const runQuery = async <TData>(
	queryMachine: ReturnType<typeof createQueryMachine<TData>>
) => {
	const gitShowMachine = createActor(queryMachine);
	gitShowMachine.start();
	return await waitForQueryDone(gitShowMachine);
};

export const waitForQueryDone = <TData>(
	query: ActorRefFromLogic<ReturnType<typeof createQueryMachine<TData>>>
) => waitFor(query, (state) => state.matches("done"));

/**
 * Creates a react-query-like query machine that handles data fetching, caching, and persistence.
 */
export function createQueryMachine<TData>(config: QueryConfig<TData>) {
	const cached = getFromCache<TData>(config.queryKey);

	return setup({
		types: {
			context: {} as QueryData<TData>,
			events: {} as { type: "invalidate" } | { type: "pause" } | { type: "resume" },
			output: {} as TData | null,
		},
		guards: {
			isEnabled: ({ context }) => context.enabled !== false,
			hasPersistence: () => isDesktopAppMode() && !!config.persistData,
			shouldEnd: ({ context }) => context.shouldEnd ?? false,
		},
		actions: {
			updateData: assign((_, params: { data: TData; source: DataSource }) => {
				if (params.source === "remote") {
					setCache(config.queryKey, params.data);
				}
				return {
					data: params.data,
					status: "success" as const,
					error: null,
					isLoading: false,
					isError: false,
					isSuccess: true,
					dataSource: params.source,
				};
			}),
			updateCachedData: assign((_, params: { cachedData: TData }) => ({
				cachedData: params.cachedData,
			})),
			setError: assign((_, params: { error: Error }) => ({
				error: params.error,
				status: "error" as const,
				isLoading: false,
				isError: true,
				isSuccess: false,
			})),
			setLoading: assign(() => ({
				isLoading: true,
				status: "loading" as const,
			})),
		},
		actors: {
			persistData: fromPromise(
				async ({ input }: { input: { data: TData | null; cachedData: TData | null } }) => {
					if (!config.persistData || !input.data || !input.cachedData) {
						return;
					}
					await config.persistData(input.data, input.cachedData);
				}
			),
			queryFn: fromPromise(async () => {
				return await config.queryFn();
			}),
			loadPersistedData: fromPromise(async () => {
				if (isDesktopAppMode() && config.loadPersistedData) {
					const load = await config.loadPersistedData();
					return load;
				}
				return null;
			}),
		},
	}).createMachine({
		id: "query",
		initial: "idle",
		context: {
			enabled: config.enabled ?? true,
			data: cached?.data ?? null,
			cachedData: cached?.data ?? null,
			error: null,
			status: cached ? "loading" : "idle",
			isLoading: false,
			isError: false,
			isSuccess: !!cached,
			dataSource: cached ? "localStorage" : null,
			shouldEnd: config.shouldEnd ?? false,
		},
		states: {
			idle: {
				always: [
					{
						guard: "isEnabled",
						target: "fetching",
					},
				],
				on: {
					invalidate: { target: "fetching" },
					pause: { target: "suspended" },
				},
			},
			suspended: {
				on: {
					resume: { target: "fetching" },
				},
			},

			fetching: {
				description: "Fetching data from the remote source",
				type: "parallel",
				states: {
					remote: {
						initial: "loading",
						states: {
							loading: {
								entry: "setLoading",
								invoke: {
									src: "queryFn",
									onDone: {
										target: "#query.success",
										actions: [
											{
												type: "updateData",
												params: ({ event }) => ({
													data: event.output,
													source: "remote",
												}),
											},
										],
									},
									onError: {
										target: "#query.error",
										actions: [
											{
												type: "setError",
												params: ({ event }) => ({
													error:
														event.error instanceof Error
															? event.error
															: new Error("Unknown error"),
												}),
											},
										],
									},
								},
							},
						},
					},
					persisted: {
						initial: "loading",
						states: {
							loading: {
								invoke: {
									src: "loadPersistedData",
									onDone: [
										{
											guard: ({ context, event }) => {
												return (
													(context.dataSource === "localStorage" ||
														context.dataSource === null) &&
													event.output !== null
												);
											},
											target: "success",
											actions: [
												{
													type: "updateData",
													params: ({ event }) => ({
														data: event.output!,
														source: "persistence",
													}),
												},
												{
													type: "updateCachedData",
													params: ({ event }) => ({
														cachedData: event.output!,
													}),
												},
											],
										},
										{ target: "complete" },
									],
									onError: {
										target: "complete",
									},
								},
							},
							success: {
								type: "final",
							},
							complete: {
								type: "final",
							},
						},
					},
				},
				onDone: [
					{
						target: "success",
						guard: ({ context }) => !!context.data,
					},
					{
						target: "error",
					},
				],
				on: {
					pause: { target: "suspended" },
				},
			},

			error: {
				after: config.refetchInterval
					? { [config.refetchInterval]: { target: "fetching" } }
					: undefined,
				on: {
					invalidate: { target: "fetching" },
					pause: { target: "suspended" },
				},
				always: {
					guard: "shouldEnd",
					target: "done",
				},
			},
			success: {
				invoke: {
					src: "persistData",
					input: ({ context }) => ({
						data: context.data,
						cachedData: context.cachedData,
					}),
				},
				on: {
					invalidate: { target: "fetching" },
					pause: { target: "suspended" },
				},
				after: config.refetchInterval
					? { [config.refetchInterval]: { target: "fetching" } }
					: undefined,
				always: {
					guard: "shouldEnd",
					target: "done",
				},
			},
			done: {
				type: "final",
			},
		},
		output: ({ context }) => {
			return context.data ?? null;
		},
	});
}
